import { Logger } from "../Misc/logger.js";
import { Observable } from "../Misc/observable.js";
import { GetDOMTextContent, IsWindowObjectExist } from "../Misc/domManagement.js";
import { ShaderProcessor } from "../Engines/Processors/shaderProcessor.js";
import { ShaderStore } from "../Engines/shaderStore.js";
import { ShaderLanguage } from "../Materials/shaderLanguage.js";
/**
 * Effect wrapping a compute shader and let execute (dispatch) the shader
 */
var ComputeEffect = /** @class */ (function () {
    /**
     * Creates a compute effect that can be used to execute a compute shader
     * @param baseName Name of the effect
     * @param options Set of all options to create the effect
     * @param engine The engine the effect is created for
     * @param key Effect Key identifying uniquely compiled shader variants
     */
    function ComputeEffect(baseName, options, engine, key) {
        if (key === void 0) { key = ""; }
        var _this = this;
        var _a, _b;
        /**
         * Name of the effect.
         */
        this.name = null;
        /**
         * String container all the define statements that should be set on the shader.
         */
        this.defines = "";
        /**
         * Callback that will be called when the shader is compiled.
         */
        this.onCompiled = null;
        /**
         * Callback that will be called if an error occurs during shader compilation.
         */
        this.onError = null;
        /**
         * Unique ID of the effect.
         */
        this.uniqueId = 0;
        /**
         * Observable that will be called when the shader is compiled.
         * It is recommended to use executeWhenCompile() or to make sure that scene.isReady() is called to get this observable raised.
         */
        this.onCompileObservable = new Observable();
        /**
         * Observable that will be called if an error occurs during shader compilation.
         */
        this.onErrorObservable = new Observable();
        /**
         * Observable that will be called when effect is bound.
         */
        this.onBindObservable = new Observable();
        /**
         * @hidden
         * Specifies if the effect was previously ready
         */
        this._wasPreviouslyReady = false;
        this._isReady = false;
        this._compilationError = "";
        /** @hidden */
        this._key = "";
        this._computeSourceCodeOverride = "";
        /** @hidden */
        this._pipelineContext = null;
        /** @hidden */
        this._computeSourceCode = "";
        this._rawComputeSourceCode = "";
        this._shaderLanguage = ShaderLanguage.WGSL;
        this.name = baseName;
        this._key = key;
        this._engine = engine;
        this.uniqueId = ComputeEffect._UniqueIdSeed++;
        this.defines = (_a = options.defines) !== null && _a !== void 0 ? _a : "";
        this.onError = options.onError;
        this.onCompiled = options.onCompiled;
        this._entryPoint = (_b = options.entryPoint) !== null && _b !== void 0 ? _b : "main";
        this._shaderStore = ShaderStore.GetShadersStore(this._shaderLanguage);
        this._shaderRepository = ShaderStore.GetShadersRepository(this._shaderLanguage);
        this._includeShaderStore = ShaderStore.GetIncludesShadersStore(this._shaderLanguage);
        var computeSource;
        var hostDocument = IsWindowObjectExist() ? this._engine.getHostDocument() : null;
        if (baseName.computeSource) {
            computeSource = "source:" + baseName.computeSource;
        }
        else if (baseName.computeElement) {
            computeSource = hostDocument ? hostDocument.getElementById(baseName.computeElement) : null;
            if (!computeSource) {
                computeSource = baseName.computeElement;
            }
        }
        else {
            computeSource = baseName.compute || baseName;
        }
        var processorOptions = {
            defines: this.defines.split("\n"),
            indexParameters: undefined,
            isFragment: false,
            shouldUseHighPrecisionShader: false,
            processor: null,
            supportsUniformBuffers: this._engine.supportsUniformBuffers,
            shadersRepository: this._shaderRepository,
            includesShadersStore: this._includeShaderStore,
            version: (this._engine.version * 100).toString(),
            platformName: this._engine.shaderPlatformName,
            processingContext: null,
            isNDCHalfZRange: this._engine.isNDCHalfZRange,
            useReverseDepthBuffer: this._engine.useReverseDepthBuffer,
        };
        this._loadShader(computeSource, "Compute", "", function (computeCode) {
            ShaderProcessor.Initialize(processorOptions);
            ShaderProcessor.PreProcess(computeCode, processorOptions, function (migratedCommputeCode) {
                _this._rawComputeSourceCode = computeCode;
                if (options.processFinalCode) {
                    migratedCommputeCode = options.processFinalCode(migratedCommputeCode);
                }
                var finalShaders = ShaderProcessor.Finalize(migratedCommputeCode, "", processorOptions);
                _this._useFinalCode(finalShaders.vertexCode, baseName);
            }, _this._engine);
        });
    }
    ComputeEffect.prototype._useFinalCode = function (migratedCommputeCode, baseName) {
        if (baseName) {
            var compute = baseName.computeElement || baseName.compute || baseName.spectorName || baseName;
            this._computeSourceCode = "//#define SHADER_NAME compute:" + compute + "\n" + migratedCommputeCode;
        }
        else {
            this._computeSourceCode = migratedCommputeCode;
        }
        this._prepareEffect();
    };
    Object.defineProperty(ComputeEffect.prototype, "key", {
        /**
         * Unique key for this effect
         */
        get: function () {
            return this._key;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * If the effect has been compiled and prepared.
     * @returns if the effect is compiled and prepared.
     */
    ComputeEffect.prototype.isReady = function () {
        try {
            return this._isReadyInternal();
        }
        catch (_a) {
            return false;
        }
    };
    ComputeEffect.prototype._isReadyInternal = function () {
        if (this._isReady) {
            return true;
        }
        if (this._pipelineContext) {
            return this._pipelineContext.isReady;
        }
        return false;
    };
    /**
     * The engine the effect was initialized with.
     * @returns the engine.
     */
    ComputeEffect.prototype.getEngine = function () {
        return this._engine;
    };
    /**
     * The pipeline context for this effect
     * @returns the associated pipeline context
     */
    ComputeEffect.prototype.getPipelineContext = function () {
        return this._pipelineContext;
    };
    /**
     * The error from the last compilation.
     * @returns the error string.
     */
    ComputeEffect.prototype.getCompilationError = function () {
        return this._compilationError;
    };
    /**
     * Adds a callback to the onCompiled observable and call the callback immediately if already ready.
     * @param func The callback to be used.
     */
    ComputeEffect.prototype.executeWhenCompiled = function (func) {
        var _this = this;
        if (this.isReady()) {
            func(this);
            return;
        }
        this.onCompileObservable.add(function (effect) {
            func(effect);
        });
        if (!this._pipelineContext || this._pipelineContext.isAsync) {
            setTimeout(function () {
                _this._checkIsReady(null);
            }, 16);
        }
    };
    ComputeEffect.prototype._checkIsReady = function (previousPipelineContext) {
        var _this = this;
        try {
            if (this._isReadyInternal()) {
                return;
            }
        }
        catch (e) {
            this._processCompilationErrors(e, previousPipelineContext);
            return;
        }
        setTimeout(function () {
            _this._checkIsReady(previousPipelineContext);
        }, 16);
    };
    ComputeEffect.prototype._loadShader = function (shader, key, optionalKey, callback) {
        if (typeof HTMLElement !== "undefined") {
            // DOM element ?
            if (shader instanceof HTMLElement) {
                var shaderCode = GetDOMTextContent(shader);
                callback(shaderCode);
                return;
            }
        }
        // Direct source ?
        if (shader.substr(0, 7) === "source:") {
            callback(shader.substr(7));
            return;
        }
        // Base64 encoded ?
        if (shader.substr(0, 7) === "base64:") {
            var shaderBinary = window.atob(shader.substr(7));
            callback(shaderBinary);
            return;
        }
        // Is in local store ?
        if (this._shaderStore[shader + key + "Shader"]) {
            callback(this._shaderStore[shader + key + "Shader"]);
            return;
        }
        if (optionalKey && this._shaderStore[shader + optionalKey + "Shader"]) {
            callback(this._shaderStore[shader + optionalKey + "Shader"]);
            return;
        }
        var shaderUrl;
        if (shader[0] === "." || shader[0] === "/" || shader.indexOf("http") > -1) {
            shaderUrl = shader;
        }
        else {
            shaderUrl = this._shaderRepository + shader;
        }
        this._engine._loadFile(shaderUrl + "." + key.toLowerCase() + ".fx", callback);
    };
    Object.defineProperty(ComputeEffect.prototype, "computeSourceCode", {
        /**
         * Gets the compute shader source code of this effect
         */
        get: function () {
            var _a, _b;
            return this._computeSourceCodeOverride ? this._computeSourceCodeOverride : (_b = (_a = this._pipelineContext) === null || _a === void 0 ? void 0 : _a._getComputeShaderCode()) !== null && _b !== void 0 ? _b : this._computeSourceCode;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ComputeEffect.prototype, "rawComputeSourceCode", {
        /**
         * Gets the compute shader source code before it has been processed by the preprocessor
         */
        get: function () {
            return this._rawComputeSourceCode;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Prepares the effect
     * @hidden
     */
    ComputeEffect.prototype._prepareEffect = function () {
        var _this = this;
        var defines = this.defines;
        var previousPipelineContext = this._pipelineContext;
        this._isReady = false;
        try {
            var engine = this._engine;
            this._pipelineContext = engine.createComputePipelineContext();
            this._pipelineContext._name = this._key;
            engine._prepareComputePipelineContext(this._pipelineContext, this._computeSourceCodeOverride ? this._computeSourceCodeOverride : this._computeSourceCode, this._rawComputeSourceCode, this._computeSourceCodeOverride ? null : defines, this._entryPoint);
            engine._executeWhenComputeStateIsCompiled(this._pipelineContext, function () {
                _this._compilationError = "";
                _this._isReady = true;
                if (_this.onCompiled) {
                    _this.onCompiled(_this);
                }
                _this.onCompileObservable.notifyObservers(_this);
                _this.onCompileObservable.clear();
                if (previousPipelineContext) {
                    _this.getEngine()._deleteComputePipelineContext(previousPipelineContext);
                }
            });
            if (this._pipelineContext.isAsync) {
                this._checkIsReady(previousPipelineContext);
            }
        }
        catch (e) {
            this._processCompilationErrors(e, previousPipelineContext);
        }
    };
    ComputeEffect.prototype._getShaderCodeAndErrorLine = function (code, error) {
        var regexp = /COMPUTE SHADER ERROR: 0:(\d+?):/;
        var errorLine = null;
        if (error && code) {
            var res = error.match(regexp);
            if (res && res.length === 2) {
                var lineNumber = parseInt(res[1]);
                var lines = code.split("\n", -1);
                if (lines.length >= lineNumber) {
                    errorLine = "Offending line [".concat(lineNumber, "] in compute code: ").concat(lines[lineNumber - 1]);
                }
            }
        }
        return [code, errorLine];
    };
    ComputeEffect.prototype._processCompilationErrors = function (e, previousPipelineContext) {
        var _a;
        var _b;
        if (previousPipelineContext === void 0) { previousPipelineContext = null; }
        this._compilationError = e.message;
        // Let's go through fallbacks then
        Logger.Error("Unable to compile compute effect:");
        Logger.Error("Defines:\r\n" + this.defines);
        if (ComputeEffect.LogShaderCodeOnCompilationError) {
            var lineErrorVertex = null, code = null;
            if ((_b = this._pipelineContext) === null || _b === void 0 ? void 0 : _b._getComputeShaderCode()) {
                _a = this._getShaderCodeAndErrorLine(this._pipelineContext._getComputeShaderCode(), this._compilationError), code = _a[0], lineErrorVertex = _a[1];
                if (code) {
                    Logger.Error("Compute code:");
                    Logger.Error(code);
                }
            }
            if (lineErrorVertex) {
                Logger.Error(lineErrorVertex);
            }
        }
        Logger.Error("Error: " + this._compilationError);
        if (previousPipelineContext) {
            this._pipelineContext = previousPipelineContext;
            this._isReady = true;
            if (this.onError) {
                this.onError(this, this._compilationError);
            }
            this.onErrorObservable.notifyObservers(this);
        }
    };
    /**
     * Release all associated resources.
     **/
    ComputeEffect.prototype.dispose = function () {
        if (this._pipelineContext) {
            this._pipelineContext.dispose();
        }
        this._engine._releaseComputeEffect(this);
    };
    /**
     * This function will add a new compute shader to the shader store
     * @param name the name of the shader
     * @param computeShader compute shader content
     */
    ComputeEffect.RegisterShader = function (name, computeShader) {
        ShaderStore.GetShadersStore(ShaderLanguage.WGSL)["".concat(name, "ComputeShader")] = computeShader;
    };
    ComputeEffect._UniqueIdSeed = 0;
    /**
     * Enable logging of the shader code when a compilation error occurs
     */
    ComputeEffect.LogShaderCodeOnCompilationError = true;
    return ComputeEffect;
}());
export { ComputeEffect };
//# sourceMappingURL=computeEffect.js.map